import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { GsFormIf } from 'libs/shared/src/components';
import { IInputIf, IInputIfChaining, IInputRender } from 'libs/shared/src/models';
import { SchemaService } from '../schemas/schema.service';

/**
 * Service for handeling tasks associated to mapping backend-inputs into schema-interfaces.
 */
@Injectable({
  providedIn: 'root'
})
export class BackendFormsService {
  // Services
  private readonly schemaService: SchemaService = inject(SchemaService);

  /**
   * Checks if the input should be visible based on the if conditions.
   * @param backendInput - The backend input to check if it should be shown.
   * @returns True if the input should be shown, false otherwise.
   */
  isInputVisible(backendInput: IInputRender, formGroup: FormGroup): boolean {
    if (!backendInput.isVisible) return true;
    const schemaIf = this.mapIfRaw(backendInput.isVisible);
    return this.schemaService.checkIfArguments(schemaIf, formGroup);
  }

  /**
   * Checks if the input should be enabled based on the if conditions.
   * @param backendInput - The backend input to check if it should be enabled.
   * @returns True if the input should be enabled, false otherwise.
   */
  isInputEnabled(backendInput: IInputRender, formGroup: FormGroup): boolean {
    if (!backendInput.updateEnabled) return true;
    const schemaIf = this.mapIfRaw(backendInput.updateEnabled);
    return this.schemaService.checkIfArguments(schemaIf, formGroup);
  }

  /**
   * Updates the disabled states of the inputs based on the constraints
   * @param inputRows - The input rows to update the disabled states for.
   * @param form - The form to update the disabled states for.
   * @param disableHidden - Also disables inputs hidden via `isVisible`, so hidden required fields don't block the form.
   */
  updateDisabledStates(inputRows: IInputRender[][], form: FormGroup, disableHidden = false): void {
    inputRows?.forEach((row) => {
      row.forEach((input) => {
        const shouldBeDisabled = !this.isInputEnabled(input, form) || (disableHidden && !this.isInputVisible(input, form));
        const formCtrl = form.get([input.name]);
        if (shouldBeDisabled && formCtrl?.enabled) {
          formCtrl.disable();
        } else if (!shouldBeDisabled && formCtrl?.disabled) {
          formCtrl.enable();
        }
      });
    });
  }

  /**
   * Maps a backend if-condition to a GsFormIf using raw field names.
   * Unlike FormsMapService.mapIf, this does NOT apply case conversion,
   * because form controls created by buildFormFromInputs use raw backend names.
   * This is Dillan
   */
  private mapIfRaw(backendIf: IInputIf): GsFormIf {
    return {
      args: backendIf.args.map((arg) => ({
        formControlName: arg.name,
        operator: arg.operator,
        value: arg.value
      })),
      chaining: backendIf.chaining === IInputIfChaining.AND ? 'and' : 'or'
    };
  }
}
